import { posKey } from '../core/board';
import type { TileColor, TileRole } from '../core/types';
import { VENUE_IDS, isVenueId, type VenueId } from '../core/venues';

export type MatchBand = 3 | 4 | 5;

export type VenueActorKind = 'snail' | 'frog' | 'ant' | 'firefly' | 'caterpillar';

/** What the board still shows for one cell when a match or clear is played. */
export interface VenueCellFact {
  key: string;
  row: number;
  col: number;
  role?: TileRole;
  /** Tile color, used so a honey jar can pull its same-type clump into the burst. */
  color?: TileColor;
  actor?: VenueActorKind;
  onTrail?: boolean;
}

export interface VenueClaim {
  venueId: VenueId;
  band: MatchBand;
  keys: readonly string[];
}

export interface ClearAssignment {
  venueId: VenueId;
  band: MatchBand;
  keys: string[];
  /** The approved clip starts on the gather frame, before the stretch. */
  soundAt: 'gather';
  /** Color-match lightning is skipped when the venue clip replaces it. */
  replaceLightning: boolean;
}

export interface VenueTimeline {
  gatherMs: number;
  stretchMs: number;
  snapMs: number;
  totalMs: number;
  particles: number;
  reduced: boolean;
}

export interface TilePose {
  dx: number;
  dy: number;
  sx: number;
  sy: number;
  rot: number;
  alpha: number;
}

/**
 * In the well, under the rival-turn veil (depth 5) and the wooden frame (depth 6).
 * The frame image and the veil are not moved. Acorns tumble inside the well so
 * the wood and the dim stay on top.
 */
export const VENUE_FX_DEPTH = 4.75;

const FULL: Record<MatchBand, { gatherMs: number; stretchMs: number; snapMs: number }> = {
  3: { gatherMs: 220, stretchMs: 260, snapMs: 220 },
  4: { gatherMs: 280, stretchMs: 340, snapMs: 280 },
  5: { gatherMs: 320, stretchMs: 420, snapMs: 360 },
};

/** Sample counts, capped so a phone cascade cannot spawn a sprite per seed. */
const PARTICLES: Record<VenueId, Record<MatchBand, number>> = {
  'mossy-meadow': { 3: 8, 4: 12, 5: 16 },
  'puddle-jump': { 3: 8, 4: 12, 5: 18 },
  'honey-hollow': { 3: 10, 4: 18, 5: 24 },
  'acorn-avalanche': { 3: 14, 4: 18, 5: 22 },
  'firefly-trails': { 3: 8, 4: 12, 5: 18 },
  'mushroom-bounce': { 3: 12, 4: 18, 5: 24 },
  'sneezy-dandelion': { 3: 8, 4: 12, 5: 18 },
  'picnic-panic': { 3: 8, 4: 12, 5: 16 },
  'rainbow-puddle': { 3: 8, 4: 12, 5: 16 },
  'caterpillar-crawl': { 3: 8, 4: 14, 5: 20 },
};

const ROLE_KEYS: Record<string, TileRole> = {
  'venue-coated': 'coated',
  'venue-mushroom': 'mushroom',
  'venue-dandelion': 'dandelion',
  'venue-wild': 'wild',
};

export function matchBand(size: number): MatchBand {
  if (size >= 5) return 5;
  if (size === 4) return 4;
  return 3;
}

/** `public/audio/venues/<slug>_m3.ogg` and the matching `.m4a`. Slug is the venue id. */
export function venueSoundFile(venueId: VenueId, size: number): string {
  return `venues/${venueId}_m${matchBand(size)}`;
}

export function venueSoundFiles(venueId: VenueId): string[] {
  return [3, 4, 5].map((band) => venueSoundFile(venueId, band));
}

export const VENUE_SOUND_FILES: readonly string[] = VENUE_IDS.flatMap((id) => venueSoundFiles(id));

export function roleFromTexture(key: string | undefined): TileRole | undefined {
  if (!key) return undefined;
  if (key.startsWith('ff-lantern-')) return 'lantern';
  if (key.startsWith('venue-ice-')) return 'ice';
  if (key.startsWith('hh-jar-')) return 'coated';
  if (key.startsWith('rp-bubble-')) return 'puddle';
  if (key.startsWith('mb-cap-') || key.startsWith('venue-mushroom')) return 'mushroom';
  return ROLE_KEYS[key];
}

export function actorKind(kind: string): VenueActorKind | undefined {
  if (kind === 'snail' || kind === 'frog' || kind === 'ant' || kind === 'firefly' || kind === 'caterpillar') return kind;
  return undefined;
}

export function cellHasPiece(venueId: VenueId, cell: VenueCellFact): boolean {
  switch (venueId) {
    case 'mossy-meadow':
      return cell.actor === 'snail';
    case 'puddle-jump':
      return cell.actor === 'frog';
    case 'honey-hollow':
      return cell.role === 'coated';
    case 'acorn-avalanche':
      return cell.role === 'ice';
    case 'firefly-trails':
      return cell.role === 'lantern';
    case 'mushroom-bounce':
      return cell.role === 'mushroom';
    case 'sneezy-dandelion':
      return cell.role === 'dandelion';
    case 'picnic-panic':
      return cell.actor === 'ant';
    case 'rainbow-puddle':
      return cell.role === 'puddle';
    case 'caterpillar-crawl':
      return cell.actor === 'caterpillar';
  }
}

/** True when this color match should skip lightning and play the venue clip. */
export function claimForMatch(
  venueId: string | null | undefined,
  cells: readonly VenueCellFact[],
  size: number,
): VenueClaim | null {
  if (!isVenueId(venueId) || cells.length === 0) return null;
  if (!cells.some((cell) => cellHasPiece(venueId, cell))) return null;
  return { venueId, band: matchBand(size), keys: cells.map((cell) => cell.key) };
}

export function shouldReplaceLightning(venueId: string | null | undefined, cells: readonly VenueCellFact[]): boolean {
  return claimForMatch(venueId, cells, cells.length) !== null;
}

const ORTHO: readonly [number, number][] = [
  [0, 1],
  [1, 0],
  [0, -1],
  [-1, 0],
];

/**
 * From each jar that is in the match, walk same-color cells that are also
 * clearing. A different color, or a cell from another match, stays on the
 * generic pop.
 */
function spreadHoneyKeys(keys: Set<string>, cells: readonly VenueCellFact[]): void {
  const byKey = new Map(cells.map((cell) => [cell.key, cell]));
  const jars = cells.filter((cell) => cell.role === 'coated' && keys.has(cell.key) && cell.color);
  const colors = new Set(jars.map((cell) => cell.color));
  const queue = cells.filter((cell) => keys.has(cell.key) && cell.color !== undefined && colors.has(cell.color));
  const seen = new Set(keys);
  let index = 0;
  while (index < queue.length) {
    const pos = queue[index];
    index += 1;
    if (!pos?.color) continue;
    for (const [dr, dc] of ORTHO) {
      const key = factKey(pos.row + dr, pos.col + dc);
      if (seen.has(key)) continue;
      const next = byKey.get(key);
      if (!next || next.color !== pos.color) continue;
      seen.add(key);
      keys.add(key);
      queue.push(next);
    }
  }
}

/**
 * Cells in this clear that play the venue animation.
 * Honey oozes across the same-color clump stuck to each jar.
 * Anything else in the same clear keeps the generic pop.
 */
export function assignVenueClear(
  venueId: string | null | undefined,
  claims: readonly VenueClaim[],
  cells: readonly VenueCellFact[],
): ClearAssignment | null {
  if (!isVenueId(venueId) || cells.length === 0) return null;
  const mine = claims.filter((claim) => claim.venueId === venueId);
  if (mine.length === 0) return null;
  const keys = new Set<string>();
  for (const claim of mine) for (const key of claim.keys) keys.add(key);
  if (venueId === 'honey-hollow') spreadHoneyKeys(keys, cells);
  if (venueId === 'acorn-avalanche') {
    const cubes = cells.filter((cell) => cell.role === 'ice' && keys.has(cell.key));
    const rows = new Set(cubes.map((cube) => cube.row));
    const cols = new Set(cubes.map((cube) => cube.col));
    for (const cell of cells) {
      if (rows.has(cell.row) || cols.has(cell.col)) keys.add(cell.key);
    }
  }
  const play = cells.filter((cell) => keys.has(cell.key)).map((cell) => cell.key);
  if (play.length === 0) return null;
  const band = mine.reduce<MatchBand>((best, claim) => (claim.band > best ? claim.band : best), 3);
  return { venueId, band, keys: play, soundAt: 'gather', replaceLightning: true };
}

export function venueTimeline(venueId: VenueId, band: MatchBand, reduced: boolean): VenueTimeline {
  if (reduced) {
    return { gatherMs: 70, stretchMs: 0, snapMs: 120, totalMs: 190, particles: 2, reduced: true };
  }
  const full = FULL[band];
  return {
    gatherMs: full.gatherMs,
    stretchMs: full.stretchMs,
    snapMs: full.snapMs,
    totalMs: full.gatherMs + full.stretchMs + full.snapMs,
    particles: PARTICLES[venueId][band],
    reduced: false,
  };
}

export function sampleVenueFrame(u: number, timeline: VenueTimeline): { phase: 'gather' | 'stretch' | 'snap'; sound: boolean } {
  const total = Math.max(1, timeline.totalMs);
  const gatherEnd = timeline.gatherMs / total;
  const stretchEnd = (timeline.gatherMs + timeline.stretchMs) / total;
  const phase = u < gatherEnd ? 'gather' : u < stretchEnd ? 'stretch' : 'snap';
  return { phase, sound: u <= 0 };
}

export function poseTile(input: {
  venueId: VenueId;
  band: MatchBand;
  index: number;
  towardX: number;
  towardY: number;
  cell: number;
  gather: number;
  stretch: number;
  snap: number;
  anchor: boolean;
}): TilePose {
  const { venueId, band, index, towardX, towardY, cell, gather, stretch, snap, anchor } = input;
  const amp = band === 5 ? 1.22 : band === 4 ? 1 : 0.82;
  const signX = Math.sign(towardX || (index % 2 === 0 ? 1 : -1));
  const lift = cell * (band === 5 ? 0.28 : band === 4 ? 0.16 : 0.06) * stretch;
  let dx = towardX * 0.34 * gather * (1 - stretch) - signX * cell * 0.12 * amp * stretch;
  let dy = towardY * 0.2 * gather * (1 - stretch) - lift;
  let sx = 1 + 0.08 * gather;
  let sy = 1 - 0.06 * gather;
  let rot = 0;
  let alpha = 1;

  if (venueId === 'honey-hollow') {
    const bulge = gather * (1 - stretch);
    sx += 0.14 * bulge;
    sy += 0.1 * bulge;
    dy += cell * 0.04 * gather * (1 - snap);
    if (anchor) rot = (index % 2 === 0 ? -1 : 1) * 16 * stretch;
  } else if (venueId === 'mushroom-bounce' && anchor) {
    sy = 1 - 0.28 * gather * (1 - stretch) + 0.34 * stretch;
    sx = 1 + 0.22 * gather * (1 - stretch);
    dy = (1 - sy) * cell * 0.32 - lift;
  } else if (venueId === 'sneezy-dandelion') {
    rot = Math.sin((gather + stretch) * 9 + index) * 7 * (gather + stretch) * (1 - snap);
  } else if (venueId === 'puddle-jump') {
    dy -= cell * 0.08 * stretch;
  }

  const shrink = 1 - snap * 0.88;
  sx *= shrink;
  sy *= shrink;
  alpha = 1 - snap;
  // The jar cracks open during the stretch, before the clump finishes fading.
  if (venueId === 'honey-hollow' && anchor && stretch > 0) alpha *= 1 - stretch;
  return { dx, dy, sx, sy, rot, alpha };
}

/** Claims for the match that is currently resolving. One clear consumes them. */
export class VenueMatchDirector {
  claims: VenueClaim[] = [];

  arm(claim: VenueClaim | null): boolean {
    if (!claim) return false;
    const known = new Set(this.claims.flatMap((item) => item.keys));
    if (claim.keys.every((key) => known.has(key))) return true;
    this.claims.push(claim);
    return true;
  }

  covers(keys: readonly string[]): boolean {
    if (this.claims.length === 0 || keys.length === 0) return false;
    const held = new Set<string>();
    for (const claim of this.claims) for (const key of claim.keys) held.add(key);
    return keys.some((key) => held.has(key));
  }

  reset(): void {
    this.claims = [];
  }
}

export function factKey(row: number, col: number): string {
  return posKey({ r: row, c: col });
}
